import { initializeApp, FirebaseApp } from 'firebase/app'
import { getAnalytics, logEvent as fbLogEvent, Analytics } from 'firebase/analytics'
import { initMetaPixel, trackMetaStandard } from './metaPixel'
import { sendToCAPI, type CAPIUserData } from './capi'
import { attributionToPayload } from './utm'

export enum AnalyticsEvent {
  SCREEN_VIEW = 'screen_view',
  CTA_CLICK = 'cta_click',
  VOUCHER_VIEW = 'voucher_view',
  CONTACT_FORM = 'contact_form',
  OUTBOUND_LINK = 'outbound_link',
  PAGE_NOT_FOUND = 'page_not_found',
}

/**
 * Meta standard conversion events used for ad attribution and optimization.
 *
 * Keep this list small — Meta's optimizer needs ≥50 conversions per event per
 * week to leave learning phase, so spreading spend across many events tanks
 * delivery. The funnel today:
 *  - LEAD: Contact Sales form submitted
 *  - CONTACT: Demo-booking intent (modal opened). Lets Meta optimize while
 *             Schedule volume is still ramping; demote/remove once Schedule
 *             clears ~50/week per ad set.
 *  - SCHEDULE: Cal.com booking confirmed
 *  - COMPLETE_REGISTRATION: Merchant signup CTA → app/onboarding
 */
export enum ConversionEvent {
  LEAD = 'Lead',
  CONTACT = 'Contact',
  SCHEDULE = 'Schedule',
  COMPLETE_REGISTRATION = 'CompleteRegistration',
}

declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, string | number | boolean>; callback?: () => void },
    ) => void
  }
}

class AnalyticsService {
  private analytics: Analytics | null = null

  initialize() {
    this.initializeFirebase()
    this.initializeMetaPixel()
  }

  private initializeFirebase() {
    try {
      const apiKey = import.meta.env.VITE_FIREBASE_API_KEY
      if (!apiKey) {
        console.debug('[Analytics] Firebase config not provided, Firebase Analytics disabled')
        return
      }

      const firebaseConfig = {
        apiKey,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
      }

      const app: FirebaseApp = initializeApp(firebaseConfig)
      this.analytics = getAnalytics(app)
      console.info('[Analytics] Firebase Analytics initialized')
    } catch (error) {
      console.warn('[Analytics] Failed to initialize Firebase Analytics', error)
    }
  }

  private initializeMetaPixel() {
    try {
      const pixelId = import.meta.env.VITE_META_PIXEL_ID
      if (!pixelId) {
        console.debug('[Analytics] Meta Pixel ID not set, Pixel disabled')
        return
      }
      initMetaPixel(pixelId)
      console.info('[Analytics] Meta Pixel initialized')
    } catch (error) {
      console.warn('[Analytics] Failed to initialize Meta Pixel', error)
    }
  }

  logEvent(name: string, params: Record<string, any> = {}) {
    console.debug('[Analytics] Event:', name, params)
    try {
      if (this.analytics) fbLogEvent(this.analytics, name, params)
    } catch (error) {
      console.warn('[Analytics] Firebase logEvent failed', name, error)
    }
    this.logPlausible(name, params)
  }

  /**
   * Forward an event to Plausible. Safe to call before the tracker script has
   * loaded — Plausible queues calls on `window.plausible.q` when the function
   * isn't ready yet, but only if the page included the snippet that defines
   * the queue. If `window.plausible` isn't defined we drop the call silently.
   *
   * Plausible CE only stores string/number/boolean prop values; we coerce
   * everything else to a string rather than letting the tracker drop it.
   */
  private logPlausible(name: string, params: Record<string, unknown>) {
    try {
      if (typeof window === 'undefined' || typeof window.plausible !== 'function') return
      const props: Record<string, string | number | boolean> = {}
      for (const [k, v] of Object.entries(params)) {
        if (v === null || v === undefined) continue
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          props[k] = v
        } else {
          props[k] = String(v)
        }
      }
      window.plausible(name, Object.keys(props).length ? { props } : undefined)
    } catch (error) {
      console.warn('[Analytics] Plausible event failed', name, error)
    }
  }

  logScreenView(screenName: string, screenClass?: string) {
    try {
      if (!this.analytics) return
      fbLogEvent(this.analytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenClass ?? screenName,
      })
    } catch (error) {
      console.warn('[Analytics] logScreenView failed', screenName, error)
    }
  }

  /**
   * Log a marketing conversion. Fires to Firebase Analytics, the client-side
   * Meta Pixel, and the server-side Meta Conversions API with a shared
   * `eventID` so Meta dedupes the Pixel + CAPI pair.
   *
   * Captured UTM/click-ID context (from `utm.ts`) is automatically attached
   * to all three sinks.
   *
   * @param event Meta standard event name (use `ConversionEvent` enum).
   * @param params Standard Meta event params (`value`, `currency`, etc.).
   *               Stored alongside the attribution context.
   * @param userData Optional PII for Meta CAPI matching. Email/phone are
   *                 hashed server-side; never sent to Firebase or Pixel
   *                 directly.
   * @returns The generated `eventID` so callers can correlate logs.
   */
  logConversion(
    event: ConversionEvent,
    params: Record<string, unknown> = {},
    userData?: CAPIUserData,
  ): string {
    const eventID = generateEventID()
    const attribution = attributionToPayload()
    // Firebase + Pixel see attribution in custom params; CAPI receives it via
    // the same path inside sendToCAPI's customData merge.
    const enrichedParams = { ...attribution, ...params }

    // Log conversion for debugging/development visibility
    console.info(`[Analytics] 🎯 Conversion: ${event}`, {
      eventID,
      params: enrichedParams,
      userData: userData ? { email: userData.email ? '***' : undefined, phone: userData.phone ? '***' : undefined } : undefined,
    })

    // 1) Firebase Analytics — same event name as the Meta standard so reports
    //    line up across systems.
    try {
      if (this.analytics) {
        fbLogEvent(this.analytics, event, enrichedParams)
      }
    } catch (error) {
      console.warn('[Analytics] Firebase conversion log failed', event, error)
    }

    // 2) Meta Pixel — eventID lets the Conversions API dedupe.
    trackMetaStandard(event, enrichedParams, eventID)

    // 3) Meta CAPI — fire-and-forget; sendToCAPI swallows errors so analytics
    //    cannot break user-facing flows.
    void sendToCAPI({
      eventName: event,
      eventID,
      customData: params,
      userData,
    })

    // 4) Plausible — independent confirmation of the funnel step. Same event
    //    name as Meta so reports align across systems.
    this.logPlausible(event, enrichedParams)

    return eventID
  }
}

function generateEventID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

export const analyticsService = new AnalyticsService()
